import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {SessionState, type LogEntry, isValidLogEntry, isPendingSession} from '../types/session'
import {useState, useCallback, useEffect} from 'react'
import {useSessionContext} from '../contexts/SessionContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {useDebounce} from '@github-ui/use-debounce'

export const CONSECUTIVE_FAILURE_TOLERANCE = 10

export interface UseSessionLogsProps {
  logsPollingInterval: number
  currentUserIsSessionOwner?: boolean
}

export const SESSION_LOGS_QUERY_KEY = 'agent-pr-session-logs'

export function generateLogsQueryKey(sessionId: string | undefined | null, repoOwner: string, repoName: string) {
  return [SESSION_LOGS_QUERY_KEY, sessionId, repoOwner, repoName]
}

/**
 * Internal query data shape stored in React Query cache.
 * Includes afterCursor for cursor-based incremental loading via Link header.
 * Hooks unwrap this to return LogEntry[] to consumers.
 */
export interface LogsQueryData {
  logs: LogEntry[]
  afterCursor: string | null
}

/**
 * Parse a Link header and extract the `after` cursor value from the rel="next" URL.
 * We extract just the cursor value (not the full URL) because the server-side path
 * differs from the client-side CAPI path.
 */
function parseAfterCursor(linkHeader: string): string | null {
  for (const part of linkHeader.split(',')) {
    const match = part.match(/<([^>]+)>;\s*rel="([^"]+)"/)
    if (match && match[1] && match[2] === 'next') {
      try {
        const url = new URL(match[1], 'http://placeholder')
        return url.searchParams.get('after')
      } catch {
        continue
      }
    }
  }
  return null
}

/**
 * Fetches logs incrementally using ?after= cursor and Link header.
 * Returns new entries and the cursor value for the next fetch.
 */
async function fetchViaIncremental(
  sessionId: string,
  previousData?: LogsQueryData,
): Promise<{newLogs: LogEntry[]; afterCursor: string | null; isIncremental: boolean}> {
  const isIncremental = !!previousData?.afterCursor
  // Build path with cursor as query param on the client-side CAPI path
  let path = `/agents/sessions/${sessionId}/logs`
  if (previousData?.afterCursor) {
    path += `?after=${encodeURIComponent(previousData.afterCursor)}`
  }

  const result = await fetchCAPI({path})
  const rawText = await result.text()

  const linkHeader = result.headers.get('Link')
  const afterCursor = linkHeader ? parseAfterCursor(linkHeader) : null

  const newLogs: LogEntry[] = []
  for (const block of rawText.split('\n\n')) {
    const trimmed = block.trim()
    if (!trimmed || trimmed === 'data: [DONE]') continue
    if (!trimmed.startsWith('data: ')) continue

    const jsonStr = trimmed.slice(6)
    try {
      const parsed = JSON.parse(jsonStr)
      if (isValidLogEntry(parsed)) {
        newLogs.push(parsed)
      }
    } catch {
      // Skip malformed SSE data lines
    }
  }

  return {newLogs, afterCursor, isIncremental}
}

/**
 * Fetches logs for a session incrementally using ?after= cursor + Link header,
 * merging new entries with the cached data.
 */
export async function fetchSessionLogs(
  sessionId: string,
  previousData: LogsQueryData | undefined,
): Promise<LogsQueryData> {
  const {newLogs, afterCursor, isIncremental} = await fetchViaIncremental(sessionId, previousData)
  const logs = isIncremental && previousData ? [...previousData.logs, ...newLogs] : newLogs
  return {logs, afterCursor}
}

export interface UseLogsQueryOptions {
  /** The session ID to fetch logs for */
  sessionId: string | undefined
  /** Whether to enable the query */
  enabled?: boolean
  /** Refetch interval in milliseconds, or false to disable polling */
  refetchInterval?: number | false
}

/**
 * Low-level hook for fetching session logs with react-query.
 * Use this when you need direct control over the query, or when you're outside SessionContext.
 */
export function useLogsQuery({sessionId, enabled = true, refetchInterval = false}: UseLogsQueryOptions) {
  const repo = useCurrentRepository()
  const queryClient = useQueryClient()

  const query = useCAPIQuery<LogsQueryData, LogEntry[]>({
    queryKey: generateLogsQueryKey(sessionId, repo.ownerLogin, repo.name),
    queryFn: async () => {
      if (!sessionId) throw new Error('No session ID provided')
      const qk = generateLogsQueryKey(sessionId, repo.ownerLogin, repo.name)
      const prev = queryClient.getQueryData<LogsQueryData>(qk)
      return fetchSessionLogs(sessionId, prev)
    },
    select: d => d.logs,
    refetchInterval,
    enabled: enabled && !!sessionId,
  })

  return query
}

export function useLogs({logsPollingInterval, currentUserIsSessionOwner}: UseSessionLogsProps) {
  const {session, logs: preQueriedLogs, logsAreLoading: inactiveLogsLoading} = useSessionContext()
  const repo = useCurrentRepository()
  const queryClient = useQueryClient()

  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const activeSessionId = session.id

  const sessionCompleted = [SessionState.Completed, SessionState.Failed, SessionState.Cancelled]
    .map(state => state.toLowerCase())
    .includes(session.state)

  // If the current user doesn't own the session, that means our inactive logs query won't return anything
  const useCachedLogs = currentUserIsSessionOwner && sessionCompleted

  const handleLogUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: generateLogsQueryKey(activeSessionId, repo.ownerLogin, repo.name),
    })
  }, [queryClient, activeSessionId, repo.ownerLogin, repo.name])

  const throttledHandleLogUpdate = useDebounce(handleLogUpdate, logsPollingInterval, {
    leading: true,
    trailing: true,
    maxWait: logsPollingInterval,
  })

  // Subscribe to alive log updates and get connection status for polling fallback
  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: throttledHandleLogUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: false,
    enableLogUpdates: true,
  })

  const queryKey = generateLogsQueryKey(activeSessionId, repo.ownerLogin, repo.name)

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- The cache lookup uses this query's existing key; preserve it during the dependency upgrade.
  const query = useCAPIQuery<LogsQueryData, LogEntry[]>({
    queryKey,
    queryFn: async () => {
      if (!activeSessionId) throw new Error('No session ID provided')
      try {
        const prev = queryClient.getQueryData<LogsQueryData>(queryKey)
        const result = await fetchSessionLogs(activeSessionId, prev)
        setConsecutiveFailures(0)
        return result
      } catch (error) {
        throw new Error(`Error fetching logs: ${error instanceof Error ? error.message : String(error)}`, {
          cause: error,
        })
      }
    },
    select: d => d.logs,
    // Use polling as fallback when Alive is not connected
    refetchInterval: aliveIsConnected ? false : logsPollingInterval,
    // Turn off log polling after 10 consecutive failures
    enabled: !isPendingSession(session) && !useCachedLogs && consecutiveFailures < CONSECUTIVE_FAILURE_TOLERANCE,
  })

  useEffect(() => {
    if (query.error) {
      // todo we should fix this later
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConsecutiveFailures(prev => prev + 1)
    }
  }, [query.error])

  const showPollingError = query.error && consecutiveFailures >= CONSECUTIVE_FAILURE_TOLERANCE

  if (useCachedLogs && (preQueriedLogs?.length || inactiveLogsLoading)) {
    return {
      data: preQueriedLogs,
      isLoading: inactiveLogsLoading,
      error: false,
      showPollingError,
    }
  } else {
    return {
      ...query,
      showPollingError,
    }
  }
}
